import React from "react";
import { Link } from "react-router-dom";
import moment from "moment/moment";
import {
  FiMapPin,
  FiEye,
  FiPhone,
  FiZap,
  FiEdit2,
  FiTrash2,
  FiExternalLink,
  FiArrowRight,
} from "react-icons/fi";

const DashboardPropertyCard = ({
  property,
  onBoost,
  onEdit,
  onDelete,
  onExtend,
  onResponse,
  onRecall,
  packageType = "BASIC LISTING",
}) => {
  if (!property) return null;

  const thumb =
    (property.DeskImg && property.DeskImg[0]) ||
    property.thumbnail ||
    (property.titleImg && property.titleImg[0]) ||
    "/assets/images/why_choose_villa.jpg";

  const photoCount = property.DeskImg ? property.DeskImg.length : 1;

  const bhkPrefix = property.noofBedrooms ? `${property.noofBedrooms} BHK ` : "";
  const subCat = property.subCategory || "Apartment";
  const cat = property.category === "Rent" ? "Rent" : "Sale";
  const locStr = [property.locality, property.city].filter(Boolean).join(", ");
  const title = property.apartment
    ? `${property.apartment} - ${bhkPrefix}${subCat} for ${cat}${locStr ? ` in ${locStr}` : ""}`
    : `${bhkPrefix}${subCat} for ${cat}${locStr ? ` in ${locStr}` : ""}`;

  const propLink = `/property-info/${
    property.apartment || property.propertyID || "property"
  }/${property.propertyID || property._id}`;

  const status = property.propertyStatus || (property.boosted ? "Boosted" : "Active");
  const statusClass =
    status === "Active"
      ? "mgh-dl-status-active"
      : status === "Boosted"
      ? "mgh-dl-status-boosted"
      : status === "Deleted" || status === "Expired"
      ? "mgh-dl-status-expired"
      : "mgh-dl-status-pending";

  const rawPrice = property.visiblePrice || property.expectedPrice || property.expectedRent;
  let displayPrice = "Price on Request";
  if (rawPrice) {
    const strPrice = String(rawPrice).trim();
    if (strPrice.startsWith("₹") || strPrice.toLowerCase().startsWith("rs")) {
      displayPrice = strPrice;
    } else {
      displayPrice = `₹ ${strPrice}`;
    }
  }

  const displayArea =
    property.superBuiltupArea || property.builtupArea || property.carpetArea || "1200";
  const areaLabel = property.superBuiltupArea
    ? "Super Built-up Area"
    : property.builtupArea
    ? "Built-up Area"
    : "Carpet Area";

  const displayPid = property.propertyID
    ? (String(property.propertyID).startsWith("MG") ? property.propertyID : `MGH-${property.propertyID}`)
    : `MGH-${property._id ? String(property._id).slice(-5) : "Listing"}`;

  return (
    <div className="mgh-dl-card">
      {/* Card Main: Thumbnail + Details */}
      <div className="mgh-dl-card-main">
        <div className="mgh-dl-thumb-wrap">
          <img src={thumb} alt={title} className="mgh-dl-thumb-img" />
          <span className="mgh-dl-photo-badge">📷 {photoCount}</span>
        </div>

        <div className="mgh-dl-details">
          <div className="mgh-dl-title-row">
            <div>
              <h3 className="mgh-dl-title">
                <Link to={propLink}>{title}</Link>
              </h3>
              <p className="mgh-dl-location-sub">
                <FiMapPin style={{ color: "#ea580c" }} />
                <span>
                  {property.locality || "Hyderabad"},{" "}
                  {property.city || "Telangana"} •{" "}
                  {property.availabilityStatus || "Ready to move"}
                </span>
              </p>
            </div>

            <div className="mgh-dl-actions">
              <Link
                to={propLink}
                className="mgh-dl-action-icon-btn"
                title="View Public Listing"
              >
                <FiExternalLink />
              </Link>
              {onEdit && (
                <button
                  type="button"
                  className="mgh-dl-action-icon-btn"
                  title="Edit Property"
                  id={`mgh-edit-btn-${property._id || property.propertyID}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(property);
                  }}
                >
                  <FiEdit2 />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="mgh-dl-action-icon-btn delete"
                  title="Delete Property"
                  id={`mgh-delete-btn-${property._id || property.propertyID}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(property);
                  }}
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
          </div>

          <div className="mgh-dl-badges-row">
            <span className={`mgh-dl-status-badge ${statusClass}`}>
              ● {status}
            </span>
            <span className="mgh-dl-pkg-badge">
              {packageType}
            </span>
          </div>

          {/* Key Metrics Bar */}
          <div className="mgh-dl-metrics-row">
            <div className="mgh-dl-metric-item">
              <span className="mgh-dl-metric-label">Price</span>
              <span className="mgh-dl-metric-value mgh-dl-metric-price">
                {displayPrice}
              </span>
            </div>

            <div className="mgh-dl-metric-item">
              <span className="mgh-dl-metric-label">{areaLabel}</span>
              <span className="mgh-dl-metric-value">
                {displayArea} sq.ft.
              </span>
            </div>

            <div className="mgh-dl-metric-item">
              <span className="mgh-dl-metric-label">Property ID</span>
              <span className="mgh-dl-metric-value">
                {displayPid}
              </span>
            </div>

            <div className="mgh-dl-metric-item">
              <span className="mgh-dl-metric-label">Posted On</span>
              <span className="mgh-dl-metric-value">
                {moment(property.createdAt).format("DD MMM YYYY")}
              </span>
            </div>

            <div className="mgh-dl-metric-item">
              <span className="mgh-dl-metric-label">Expires On</span>
              <span className="mgh-dl-metric-value">
                {moment(property.ExpiryDate).format("DD MMM YYYY")}
                {onExtend && (
                  <span
                    className="mgh-dl-extend-link"
                    onClick={() => onExtend(property)}
                  >
                    Extend
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance & Inquiries Section */}
      <div className="mgh-dl-performance-row">
        <div className="mgh-dl-score-box">
          <div className="mgh-dl-score-gauge">95%</div>
          <div className="mgh-dl-score-texts">
            <h5>Listing Health Score: Good</h5>
            <p>Add 3+ high-res photos to rank in top 5% search results</p>
          </div>
        </div>

        <div className="mgh-dl-views-box">
          <div className="mgh-dl-view-pills">
            <span className="mgh-dl-view-pill">
              <FiEye style={{ marginRight: "4px" }} />
              {property.Detailsview || property.noofPropertyViewed || 0} Detail Views
            </span>
            <span className="mgh-dl-view-pill">
              <FiPhone style={{ marginRight: "4px" }} />
              {property.Contactview || 0} Contacts Viewed
            </span>
          </div>

          {onResponse && (
            <button
              type="button"
              className="mgh-dl-response-btn"
              onClick={(e) => onResponse(e, property)}
            >
              View {property.noofPropertyViewed || 0} Inquiries & Responses →
            </button>
          )}
        </div>

        {onBoost && (
          <button
            type="button"
            className="mgh-dl-boost-btn"
            id={`mgh-boost-btn-${property._id || property.propertyID}`}
            onClick={(e) => {
              e.stopPropagation();
              onBoost(property);
            }}
          >
            <FiZap />
            <span>Boost Listing</span>
          </button>
        )}

        {onRecall && (
          <button
            type="button"
            className="mgh-dl-recall-btn"
            onClick={() => onRecall(property)}
            title="Recall this listing"
          >
            🔄 Recall Listing
          </button>
        )}
      </div>

      {/* Bottom Lead Banner */}
      <div className="mgh-dl-card-footer">
        <span>
          💡 Need more verified buyers or tenants? Connect directly with active seekers.
        </span>
        <Link to="/leadtosearch" className="mgh-dl-getleads-btn">
          <span>Search Matching Leads</span>
          <FiArrowRight />
        </Link>
      </div>
    </div>
  );
};

export default DashboardPropertyCard;
